import { useCallback, useEffect, useState } from "react";

const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FACEBOOK_SDK_VERSION = "v20.0";

let sdkLoadPromise = null;

const loadFacebookSdk = (appId) => {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Facebook SDK requires a browser environment"));
	}

	if (window.FB) {
		return Promise.resolve(window.FB);
	}

	if (sdkLoadPromise) {
		return sdkLoadPromise;
	}

	sdkLoadPromise = new Promise((resolve, reject) => {
		window.fbAsyncInit = function () {
			window.FB.init({
				appId,
				cookie: true,
				xfbml: false,
				version: FACEBOOK_SDK_VERSION,
			});
			resolve(window.FB);
		};

		const existing = document.getElementById("facebook-jssdk");
		if (existing) {
			return;
		}

		const script = document.createElement("script");
		script.id = "facebook-jssdk";
		script.src = FACEBOOK_SDK_SRC;
		script.async = true;
		script.defer = true;
		script.onerror = () => {
			sdkLoadPromise = null;
			reject(new Error("Failed to load Facebook SDK"));
		};
		document.body.appendChild(script);
	});

	return sdkLoadPromise;
};

/**
 * Load the Facebook JS SDK once and expose a Promise-based login helper.
 */
const useFacebookSdk = () => {
	const appId = process.env.REACT_APP_FACEBOOK_APP_ID || "";
	const [ready, setReady] = useState(Boolean(typeof window !== "undefined" && window.FB));
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!appId) {
			setError(new Error("Facebook App ID is not configured"));
			return;
		}

		let cancelled = false;

		loadFacebookSdk(appId)
			.then(() => {
				if (!cancelled) {
					setReady(true);
					setError(null);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(err);
					setReady(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [appId]);

	const login = useCallback(() => {
		return new Promise((resolve, reject) => {
			if (!appId) {
				reject(new Error("Facebook App ID is not configured"));
				return;
			}

			loadFacebookSdk(appId)
				.then((FB) => {
					FB.login(
						(response) => {
							if (response?.authResponse?.accessToken) {
								resolve(response.authResponse.accessToken);
								return;
							}
							reject(new Error("Facebook login was cancelled or failed"));
						},
						{ scope: "public_profile,email" }
					);
				})
				.catch(reject);
		});
	}, [appId]);

	return { ready, error, login, enabled: Boolean(appId) };
};

export default useFacebookSdk;
